import { createSlice } from '@reduxjs/toolkit'
import { governanceInitialState as initialState } from '../initialState/governanceState'
import { apiCallBegan } from '../api'

// The endpoints below are served by internal-portal-pixm-fa `governance` — a
// single multi-method folder routed by resource. Each response uses the
// { data: [item], errors: [] } envelope, so reducers read data[0].
const firstOf = (action) => {
  const body = action.payload.response
  const arr = body?.data ?? body ?? null
  return Array.isArray(arr) ? (arr[0] ?? null) : arr
}

const slice = createSlice({
  name: 'governance',
  initialState: initialState,
  reducers: {
    // ----- Published policy (header + published version + rules) -----
    policyRequested: (g) => { g.policyLoading = true; g.error = false; g.message = '' },
    policyReceived: (g, action) => {
      g.policyLoading = false
      const item = firstOf(action)
      // DATA carries `rules`; WEB/AI carry `apps`. Normalize both to arrays so
      // whichever the active control type uses is always a safe list (additive —
      // the DATA `rules` behavior is unchanged).
      g.policy = item
        ? { ...item, rules: Array.isArray(item.rules) ? item.rules : [], apps: Array.isArray(item.apps) ? item.apps : [] }
        : null
      // Tag which control type this response is for (DATA/WEB/AI all share this one
      // slice). Consumers ignore data whose type ≠ the active type, so an
      // out-of-order response for a different type can't be rendered. Set on
      // RECEIVE only — during an in-flight refetch the stale data is gated by
      // policyLoading, and tagging it early would falsely pass the guard.
      g.policyType = action.reducerData?.controlType ?? 'DATA'
    },
    policyFailed: (g, action) => {
      g.policyLoading = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to load governance policy'
    },

    // ----- Draft (version + rules) -----
    draftRequested: (g) => { g.draftLoading = true; g.error = false; g.message = '' },
    draftReceived: (g, action) => {
      g.draftLoading = false
      const item = firstOf(action)
      g.draft = item
        ? { ...item, rules: Array.isArray(item.rules) ? item.rules : [], apps: Array.isArray(item.apps) ? item.apps : [] }
        : null
      // See policyReceived — tags the draft with its control type for the guard.
      g.draftType = action.reducerData?.controlType ?? 'DATA'
    },
    draftFailed: (g, action) => {
      g.draftLoading = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to load draft'
    },

    // ----- WEB/AI sites list (one entry per site policy) -----
    sitesRequested: (g) => { g.sitesLoading = true; g.error = false; g.message = '' },
    sitesReceived: (g, action) => {
      g.sitesLoading = false
      const body = action.payload.response
      const arr = body?.data ?? []
      g.sites = Array.isArray(arr) ? arr : []
      g.sitesType = action.reducerData?.controlType ?? null
    },
    sitesFailed: (g, action) => {
      g.sitesLoading = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to load sites'
    },

    // ----- WEB Unsanctioned Apps discovery list (read-only; §9) -----
    unsancAppsRequested: (g) => { g.unsancAppsLoading = true },
    unsancAppsReceived: (g, action) => {
      g.unsancAppsLoading = false
      const item = firstOf(action)
      g.unsancApps = item && Array.isArray(item.records) ? item.records : []
      g.unsancAppsTotalCount = item?.totalCount ?? 0
      g.unsancAppsLookbackDays = item?.lookbackDays ?? null
    },
    unsancAppsFailed: (g) => {
      g.unsancAppsLoading = false
      g.unsancApps = []
      g.unsancAppsTotalCount = 0
    },
    setUnsancAppsFilters: (g, action) => { g.unsancAppsFilters = { ...g.unsancAppsFilters, ...action.payload } },

    // ----- Extension Controls -----
    extensionPoliciesRequested: (g) => { g.extensionPoliciesLoading = true },
    extensionPoliciesReceived: (g, action) => {
      g.extensionPoliciesLoading = false
      // PIXMC-6539: now the house PAGED shape at data[0] = { records, totalCount }, like
      // extensionLookup — so read it through firstOf(), not off body.data as an array.
      const item = firstOf(action)
      g.extensionPolicies = item && Array.isArray(item.records) ? item.records : []
      g.extensionPolicyTotalCount = item?.totalCount ?? 0
    },
    extensionPoliciesFailed: (g, action) => {
      g.extensionPoliciesLoading = false
      g.extensionPolicies = []
      g.extensionPolicyTotalCount = 0
      g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to load extension policies'
    },

    // The stat-tile totals (PIXMC-6539), from the dedicated /counts endpoint. Deliberately NOT
    // cleared to zero on request: the tiles should hold their last value through a refetch rather
    // than flicker to 0.
    extensionCountsRequested: (g) => { g.extensionCountsLoading = true },
    extensionCountsReceived: (g, action) => {
      g.extensionCountsLoading = false
      const item = firstOf(action)
      g.extensionCounts = {
        denied: item?.denied ?? 0,
        sanctioned: item?.sanctioned ?? 0,
        unsanctioned: item?.unsanctioned ?? 0,
      }
    },
    extensionCountsFailed: (g) => { g.extensionCountsLoading = false },

    extensionPredefinedRulesRequested: (g) => { g.extensionPredefinedRulesLoading = true },
    extensionPredefinedRulesReceived: (g, action) => {
      g.extensionPredefinedRulesLoading = false
      const body = action.payload.response
      g.extensionPredefinedRules = Array.isArray(body?.data) ? body.data : []
    },
    extensionPredefinedRulesFailed: (g) => {
      g.extensionPredefinedRulesLoading = false
      g.extensionPredefinedRules = []
    },

    extensionLookupRequested: (g) => { g.extensionLookupLoading = true },
    extensionLookupReceived: (g, action) => {
      g.extensionLookupLoading = false
      const item = firstOf(action)
      g.extensionLookup = item && Array.isArray(item.records) ? item.records : []
      g.extensionLookupTotalCount = item?.totalCount ?? 0
    },
    extensionLookupFailed: (g) => {
      g.extensionLookupLoading = false
      g.extensionLookup = []
      g.extensionLookupTotalCount = 0
    },

    // logicalExtensionId -> extensionName, for the Incident Feed's Extension Violations tab.
    // Reduced to a plain map here rather than in the component so every consumer gets the same
    // shape and the records array is not retained.
    extensionNamesReceived: (g, action) => {
      // firstOf, NOT action.payload.data: the api middleware dispatches
      // `payload: { response, request }`, so the body is one level deeper. Reading it directly
      // silently yielded an empty map and every row fell back to its raw logical id.
      const item = firstOf(action)
      const records = (item && Array.isArray(item.records)) ? item.records : []
      const names = {}
      records.forEach((r) => {
        if (r.logicalExtensionId) names[r.logicalExtensionId] = r.extensionName || null
      })
      g.extensionNames = names
    },
    // Deliberately NOT cleared on failure: a stale name map renders slightly out-of-date names,
    // which is better than every row falling back to a raw logical id.
    extensionNamesFailed: () => {},

    extensionEvaluationRequested: (g) => { g.extensionEvaluationLoading = true },
    extensionEvaluationReceived: (g, action) => {
      g.extensionEvaluationLoading = false
      const item = firstOf(action)
      g.extensionEvaluation = item && Array.isArray(item.records) ? item.records : []
    },
    extensionEvaluationFailed: (g) => {
      // Deliberately does NOT clear extensionLookup: if only the aggregate half
      // fails, the inventory is still worth showing with blank counts rather than
      // an empty table that reads as "no extensions installed".
      g.extensionEvaluationLoading = false
      g.extensionEvaluation = []
    },

    setExtensionFilters: (g, action) => { g.extensionFilters = { ...g.extensionFilters, ...action.payload } },
    // The TOP table's own filter/paging state (PIXMC-6539), kept apart from extensionFilters so a
    // change here never refetches or disturbs the Policy Evaluation block.
    setExtensionPolicyFilters: (g, action) => { g.extensionPolicyFilters = { ...g.extensionPolicyFilters, ...action.payload } },

    extensionRuleAttachRequested: (g) => {
      g.attachingExtensionRule = true; g.error = false; g.message = ''
      g.extensionRuleErrorCode = null; g.extensionRuleErrorDesc = null
    },
    extensionRuleAttachSucceeded: (g) => {
      g.attachingExtensionRule = false; g.statusCode = 200
      g.message = 'Rule attached. Approve and publish to make it live.'
    },
    extensionRuleAttachFailed: (g, action) => {
      g.attachingExtensionRule = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      // The API returns the real reason in errors[] (ERR_GOV_019 "an extension policy
      // for this extension already exists", ERR_GOV_005 "rules are editable only while
      // DRAFT", …). Reading only payload.message threw all of that away and left the
      // admin with a generic failure line they could not act on.
      const errs = action.payload?.errors
      const first = Array.isArray(errs) && errs.length ? errs[0] : null
      g.extensionRuleErrorCode = first?.code ?? null
      g.extensionRuleErrorDesc = first?.desc ?? action.payload?.message ?? 'Failed to attach the rule.'
      g.message = g.extensionRuleErrorDesc
    },
    clearExtensionRuleError: (g) => { g.extensionRuleErrorCode = null; g.extensionRuleErrorDesc = null },

    // ----- Create / resume draft -----
    draftCreateRequested: (g) => { g.creatingDraft = true; g.error = false; g.message = '' },
    draftCreateSucceeded: (g) => { g.creatingDraft = false; g.statusCode = 200; g.message = 'Draft opened' },
    draftCreateFailed: (g, action) => {
      g.creatingDraft = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to open draft'
    },

    // ----- WEB/AI new-site creation (PIXMC-6083 §10.4) -----
    // A dedicated pair of fields, deliberately separate from draftCreateFailed above:
    // createGovernanceSite shares that action's onStart/onError with createGovernanceDraft
    // and createGovernanceSiteDraft (all three open/resume a draft), so a component that
    // reacted to the shared message/error instead would also fire for those unrelated
    // flows. createSiteErrorCode/-Desc are written only here, and only read by the site
    // creator, so a stale value can never bleed into a different screen.
    createSiteRequested: (g) => { g.creatingDraft = true; g.createSiteErrorCode = null; g.createSiteErrorDesc = null },
    createSiteSucceeded: (g) => { g.creatingDraft = false },
    createSiteFailed: (g, action) => {
      g.creatingDraft = false
      const errs = action.payload?.errors
      const first = Array.isArray(errs) && errs.length ? errs[0] : null
      g.createSiteErrorCode = first?.code ?? null
      g.createSiteErrorDesc = first?.desc ?? action.payload?.message ?? 'Failed to create the site.'
    },
    clearCreateSiteError: (g) => { g.createSiteErrorCode = null; g.createSiteErrorDesc = null },

    // ----- Rule create / update / delete -----
    ruleSaveRequested: (g) => { g.savingRule = true; g.error = false; g.message = '' },
    ruleSaveSucceeded: (g) => { g.savingRule = false; g.statusCode = 200; g.message = 'Rule saved' },
    ruleSaveFailed: (g, action) => {
      g.savingRule = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to save rule'
    },

    // ----- Publish / rollback -----
    publishRequested: (g) => { g.publishing = true; g.error = false; g.message = '' },
    publishSucceeded: (g) => { g.publishing = false; g.statusCode = 200; g.message = 'Policy published' },
    publishFailed: (g, action) => {
      g.publishing = false; g.error = true
      g.statusCode = action.payload?.statusCode ?? 500
      g.message = action.payload?.message ?? 'Failed to publish policy'
    },

    // ----- Default rules template (for new-policy display, never written to DB) -----
    defaultRulesRequested: (g) => { g.defaultRulesLoading = true; },
    defaultRulesReceived: (g, action) => {
      g.defaultRulesLoading = false;
      const body = action.payload.response;
      const arr = body?.data ?? [];
      g.defaultRules = Array.isArray(arr) ? arr : [];
    },
    defaultRulesFailed: (g) => { g.defaultRulesLoading = false; },

    clearGovernanceStatus: (g) => { g.statusCode = null; g.message = ''; g.error = false },
  },
})

export const {
  policyRequested, policyReceived, policyFailed,
  draftRequested, draftReceived, draftFailed,
  sitesRequested, sitesReceived, sitesFailed,
  unsancAppsRequested, unsancAppsReceived, unsancAppsFailed, setUnsancAppsFilters,
  extensionPoliciesRequested, extensionPoliciesReceived, extensionPoliciesFailed,
  extensionCountsRequested, extensionCountsReceived, extensionCountsFailed,
  extensionPredefinedRulesRequested, extensionPredefinedRulesReceived, extensionPredefinedRulesFailed,
  extensionLookupRequested, extensionLookupReceived, extensionLookupFailed,
  extensionNamesReceived, extensionNamesFailed,
  extensionEvaluationRequested, extensionEvaluationReceived, extensionEvaluationFailed,
  setExtensionFilters, setExtensionPolicyFilters,
  extensionRuleAttachRequested, extensionRuleAttachSucceeded, extensionRuleAttachFailed,
  clearExtensionRuleError,
  draftCreateRequested, draftCreateSucceeded, draftCreateFailed,
  createSiteRequested, createSiteSucceeded, createSiteFailed, clearCreateSiteError,
  ruleSaveRequested, ruleSaveSucceeded, ruleSaveFailed,
  publishRequested, publishSucceeded, publishFailed,
  defaultRulesRequested, defaultRulesReceived, defaultRulesFailed,
  clearGovernanceStatus,
} = slice.actions

export default slice.reducer

// ===========================================================================
// Thunks — internal-portal-pixm-fa `governance` (customer-scoped, table-direct).
//
// controlType (default 'DATA') selects the backend route segment:
//   DATA → /governance/…            (legacy path; unchanged, so DATA is byte-identical)
//   WEB  → /governance/web/…
//   AI   → /governance/ai/…
// The API's parseRoute treats a leading data|web|ai segment as the policy type
// and everything else as a legacy DATA URL, so omitting the segment stays DATA.
// ===========================================================================

// Route segment for a control type ('' for DATA to preserve the legacy URLs).
const seg = (controlType) =>
  controlType && controlType !== 'DATA' ? `/${String(controlType).toLowerCase()}` : ''

// GET published policy (header + published version + rules [DATA] / apps [WEB/AI]).
export const getGovernancePolicy = (customerId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}`, method: 'GET',
  reducerData: { controlType },
  onStart: policyRequested.type, onSuccess: policyReceived.type, onError: policyFailed.type,
})

// GET the open draft (version + rules/apps); version is null when none is open.
export const getGovernanceDraft = (customerId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`, method: 'GET',
  reducerData: { controlType },
  onStart: draftRequested.type, onSuccess: draftReceived.type, onError: draftFailed.type,
})

// POST create/resume a draft (empty on first use, else resume open draft or clone published).
export const createGovernanceDraft = (customerId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`, method: 'POST',
  onStart: draftCreateRequested.type, onSuccess: draftCreateSucceeded.type, onError: draftCreateFailed.type,
})

// POST add a rule to the draft.
export const addGovernanceRule = (customerId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/rules`, data: body, method: 'POST',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// PUT update a rule in the draft.
export const updateGovernanceRule = (customerId, ruleId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/rules/${ruleId}`, data: body, method: 'PUT',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// DELETE a custom rule from the draft (policyVersionId passed as a query param).
export const deleteGovernanceRule = (customerId, ruleId, policyVersionId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/rules/${ruleId}`,
  filters: { policyVersionId }, method: 'DELETE',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// DELETE discard the working draft (revert to the published policy). The draft's
// version + child rows are soft-deleted; the live published version is untouched.
export const discardGovernanceDraft = (customerId, policyVersionId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`,
  filters: { policyVersionId }, method: 'DELETE',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// POST approve the draft (moves DRAFT → APPROVED; only an APPROVED version can be published).
export const approveGovernancePolicy = (customerId, policyVersionId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/approve`, data: { policyVersionId }, method: 'POST',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// POST reject a version (moves DRAFT or APPROVED → REJECTED; body carries the reason).
// The API accepts reject from either state — it is not limited to APPROVED versions.
export const rejectGovernancePolicy = (customerId, policyVersionId, rejectionReason, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/reject`, data: { policyVersionId, rejectionReason }, method: 'POST',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// POST publish the draft (atomic go-live; only valid when the version is APPROVED).
export const publishGovernancePolicy = (customerId, policyVersionId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/publish`, data: { policyVersionId }, method: 'POST',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// POST roll back to a prior version.
export const rollbackGovernancePolicy = (customerId, policyId, toVersionId, controlType = 'DATA') => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/rollback`, data: { policyId, toVersionId }, method: 'POST',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// ===========================================================================
// Extension Controls thunks — the "policy = extension" model.
//
// Each extension is its own policy with its own version chain, so attaching or
// detaching one extension's rule versions no other extension. seg('EXTENSION')
// resolves to /extension, so the existing governance route serves these with no
// new backend function.
//
// NOTHING HERE AUTHORS A RULE. The rule definitions are seeded, read-only database
// rows; a PixmAdmin only SELECTS one and attaches it. There is deliberately no
// create/update-rule thunk for extensions, and the action and disposition are
// derived server-side from the chosen rule — the request body cannot override them.
//
// approve / reject / publish / rollback need no extension-specific thunks: the
// existing ones already take a controlType and are control-type agnostic end to end.
// ===========================================================================

// GET the Extension Policy block. One row per extension policy with its attached
// rule, ordered server-side: default rule first, then Sanctioned, Denied,
// Unsanctioned, name-ordered within each group.
export const listExtensionPolicies = (customerId, filters = {}) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension`, method: 'GET',
  filters: {
    page: filters.page || undefined,
    pageSize: filters.pageSize || undefined,
    search: filters.search || undefined,
    disposition: filters.disposition || undefined,
  },
  onStart: extensionPoliciesRequested.type,
  onSuccess: extensionPoliciesReceived.type,
  onError: extensionPoliciesFailed.type,
})

// The three stat-tile totals — a SEPARATE endpoint from the paged list, so the tiles are
// decoupled from the table's search/pagination/filter (PIXMC-6539). No query params.
export const getExtensionCounts = (customerId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension/counts`, method: 'GET',
  onStart: extensionCountsRequested.type,
  onSuccess: extensionCountsReceived.type,
  onError: extensionCountsFailed.type,
})

// GET the fixed catalogue that can be attached. Read-only: this is the ONLY way to
// discover what is attachable, and it is the seeded set — not user-authored.
export const listExtensionPredefinedRules = (customerId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension/default-rules`, method: 'GET',
  onStart: extensionPredefinedRulesRequested.type,
  onSuccess: extensionPredefinedRulesReceived.type,
  onError: extensionPredefinedRulesFailed.type,
})

// POST attach a predefined rule. Body carries ONLY the selection — scanRuleId, the
// extension it targets, and an optional justification. The server derives the action
// from the seeded rule, so an Allow rule cannot be turned into a Deny one here.
// Creates the policy and its first DRAFT; approve + publish still have to follow
// before it is live.
export const attachExtensionRule = (customerId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension/draft`,
  data: body, method: 'POST',
  onStart: extensionRuleAttachRequested.type,
  onSuccess: extensionRuleAttachSucceeded.type,
  onError: extensionRuleAttachFailed.type,
})

// DELETE detach the attached rule. Never deletes a rule DEFINITION. The server
// refuses to detach the default "Any other extension" rule for anyone but a
// PixmAdmin, since doing so would end all extension reporting for the customer.
// DELETE the whole extension policy — every rule and every version, published ones
// included. This is what the landing page's Remove uses: removing an extension from
// governance must work in ANY state, and the detach above deliberately does not.
// Mirrors deleteGovernanceSite for Web/AI.
export const deleteExtensionPolicy = (customerId, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension`,
  filters: { policyId }, method: 'DELETE',
  onStart: extensionRuleAttachRequested.type,
  onSuccess: extensionRuleAttachSucceeded.type,
  onError: extensionRuleAttachFailed.type,
})

// POST open a NEW DRAFT on an extension that is already governed. Same route and same
// { policyId } body shape as createGovernanceSiteDraft above — the backend splits on the
// body, not the URL.
//
// This is the only way to change a PUBLISHED extension policy. Every extension write is
// DRAFT-only server-side, so without this a live rule could not be disabled and a Denied
// extension could not be switched to Sanctioned. The clone lands at VersionNumber + 1,
// which is the version bump those changes have to carry; approve + publish still follow.
//
// Returns the version to edit. If a DRAFT is already open it hands that one back
// (resumed: true) rather than stacking a second draft on the policy.
export const createExtensionDraft = (customerId, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension/draft`,
  data: { policyId }, method: 'POST',
  onStart: extensionRuleAttachRequested.type,
  onSuccess: extensionRuleAttachSucceeded.type,
  onError: extensionRuleAttachFailed.type,
})

// PUT edit an attached rule — body { decisionReason?, isActive?, scanRuleId? }.
// scanRuleId may only move between the two seeded rules paired to this SAME extension
// (its Deny rule and its Allow rule): that is the Denied <-> Sanctioned switch. The
// extension a rule governs is still fixed.
// Allowed only while the version is DRAFT, and the server assigns a NEW ruleId on
// every edit, so the caller must refetch rather than reuse the id it sent.
export const updateExtensionRule = (customerId, ruleId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/extension/rules/${ruleId}`,
  data: body, method: 'PUT',
  onStart: extensionRuleAttachRequested.type,
  onSuccess: extensionRuleAttachSucceeded.type,
  onError: extensionRuleAttachFailed.type,
})

// GET the inventory half of the Policy Evaluation block: names, publisher,
// disposition, surrogate flag. Configuration DB. No device counts.
export const listExtensionLookup = (customerId, filters = {}) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/extension-lookup`,
  filters: { orderBy: 'policyBlock', ...filters }, method: 'GET',
  onStart: extensionLookupRequested.type,
  onSuccess: extensionLookupReceived.type,
  onError: extensionLookupFailed.type,
})

// GET just the id -> name map, for the Incident Feed's Extension Violations tab. A violation
// row carries only the logical id (AppIdentifier) and the name lives in a different DATABASE, so
// there is no SQL join available -- the two are matched client-side.
//
// pageSize 200 is MAX_PAGE_SIZE, clamped server-side. A customer with more than 200 distinct
// extensions would render logical ids for the overflow; at the expected 5-10 per device that is a
// long way off, but it is a real ceiling rather than an unbounded fetch.
//
// Writes to its OWN slice, never extensionLookup: that one is filtered by the Extension Controls
// screen's search/disposition and drives its stat-card counts, so sharing it would make this
// fetch visibly corrupt that screen's numbers.
export const listExtensionNames = (customerId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/extension-lookup`,
  filters: { page: 1, pageSize: 200 }, method: 'GET',
  onSuccess: extensionNamesReceived.type,
  onError: extensionNamesFailed.type,
})

// GET the aggregate half: unique DEVICE count, first/last seen, folded status.
// Customer DB, over the violation ledger. Joined to the lookup in the component,
// because the two halves live in different databases and cannot be joined in SQL.
export const listExtensionEvaluation = (customerId, filters = {}) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/unsanctioned-extensions`,
  filters, method: 'GET',
  onStart: extensionEvaluationRequested.type,
  onSuccess: extensionEvaluationReceived.type,
  onError: extensionEvaluationFailed.type,
})

// ===========================================================================
// WEB / AI "policy = site" thunks.
// Each site is its own policy: a landing row with its own version state, and an
// editor keyed by policyId. A site carries a copy of the DATA rule set (edited
// per-rule on SANCTIONED sites) + its domain + conditional-access email domains.
// ===========================================================================

// GET list all WEB/AI sites for the customer (landing). Backend: GET /governance{seg}
// with no policyId -> listSites (array of { policyId, appDomain, dispositionCode,
// emailDomains, publishedVersion, openDraft }).
export const listGovernanceSites = (customerId, controlType) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}`, method: 'GET',
  reducerData: { controlType },
  onStart: sitesRequested.type, onSuccess: sitesReceived.type, onError: sitesFailed.type,
})

// GET one published site (version + app/domain + email domains + copied rule set).
export const getGovernanceSite = (customerId, controlType, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}`,
  filters: { policyId }, method: 'GET',
  reducerData: { controlType },
  onStart: policyRequested.type, onSuccess: policyReceived.type, onError: policyFailed.type,
})

// GET the open draft of one site (version + app/domain + email domains + rules).
export const getGovernanceSiteDraft = (customerId, controlType, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`,
  filters: { policyId }, method: 'GET',
  reducerData: { controlType },
  onStart: draftRequested.type, onSuccess: draftReceived.type, onError: draftFailed.type,
})

// POST create a NEW site: server derives PolicyName, opens the first draft, and
// copies the published DATA rule set. Body: { appDomain, dispositionCode, emailDomains[] }.
// Uses its own onError (createSiteFailed) rather than draftCreateFailed — see that
// reducer's comment for why the duplicate-domain error (§10.4) needs a dedicated field.
export const createGovernanceSite = (customerId, controlType, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`, data: body, method: 'POST',
  onStart: createSiteRequested.type, onSuccess: createSiteSucceeded.type, onError: createSiteFailed.type,
})

// POST open a re-copying draft for an EXISTING site (fresh reset of per-rule edits).
// Body: { policyId }.
export const createGovernanceSiteDraft = (customerId, controlType, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/draft`, data: { policyId }, method: 'POST',
  onStart: draftCreateRequested.type, onSuccess: draftCreateSucceeded.type, onError: draftCreateFailed.type,
})

// DELETE a whole site (policyId passed as a query param).
export const deleteGovernanceSite = (customerId, controlType, policyId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}`,
  filters: { policyId }, method: 'DELETE',
  onStart: publishRequested.type, onSuccess: publishSucceeded.type, onError: publishFailed.type,
})

// PUT edit a copied site rule's action/enabled (SANCTIONED sites only). Body:
// { policyVersionId, actionCode?, isActive?, channelCode? } (channelCode is the AI
// PROMPT/RESPONSE row being edited; omitted/ANY for Web).
export const updateGovernanceSiteRule = (customerId, controlType, ruleId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/rules/${ruleId}`, data: body, method: 'PUT',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// ----- WEB / AI app/site metadata (domain + disposition + email domains) -----
// controlType is WEB or AI. The body carries the app: appDomain, dispositionCode,
// emailDomains[] (conditional-access, SANCTIONED-only), policyVersionId.

// POST add an app/alias to a site draft.
export const addGovernanceApp = (customerId, controlType, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/apps`, data: body, method: 'POST',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// PUT update an app/site's domain/disposition/email domains in the draft.
export const updateGovernanceApp = (customerId, controlType, appId, body) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/apps/${appId}`, data: body, method: 'PUT',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// DELETE an app/alias from the draft (policyVersionId passed as a query param).
export const deleteGovernanceApp = (customerId, controlType, appId, policyVersionId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance${seg(controlType)}/apps/${appId}`,
  filters: { policyVersionId }, method: 'DELETE',
  onStart: ruleSaveRequested.type, onSuccess: ruleSaveSucceeded.type, onError: ruleSaveFailed.type,
})

// GET the 8 built-in default rule templates (reads MST_SCAN_RULE, never writes to policy tables).
// Used to pre-populate a brand-new policy editor before Save Draft is clicked.
export const getGovernanceDefaultRules = (customerId) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/governance/default-rules`, method: 'GET',
  onStart: defaultRulesRequested.type, onSuccess: defaultRulesReceived.type, onError: defaultRulesFailed.type,
})

// ===========================================================================
// WEB Unsanctioned Apps discovery list (read-only; PIXMC-6083 §9/§10) — domains the
// daily job found but no admin has sanctioned/denied yet. Web-only (D13/§10.1).
//
// Route is /unsanctioned-apps, NOT nested under /governance/web/... — an earlier
// revision put it there, which collided with the `governance` endpoint's own
// {controlType?}/{resource?}/{resourceId?} wildcard route (confirmed against a real
// Dev deployment: the Functions host resolved the ambiguity in favor of `governance`
// instead of this endpoint). See consumer-v2/docs/GOVERNANCE_DATA_CONTROLS.md.
// ===========================================================================

// GET the paginated/searchable/sortable discovery list.
export const listUnsanctionedApps = (customerId, filters) => apiCallBegan({
  urlPath: `/portal/customers/${customerId}/unsanctioned-apps`, method: 'GET', filters,
  onStart: unsancAppsRequested.type, onSuccess: unsancAppsReceived.type, onError: unsancAppsFailed.type,
})

export const clearGovernanceMessage = () => clearGovernanceStatus()
